import {
    Checkbox,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import noop from "lib/noop"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BlockKeyboard } from "lib/@components/block-clicks"
import Iconify from "minimals-template/components/Iconify"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { ScheduleSummary } from "routes/schedule/plugins/ai-summary/ai-summary.runtime"
import { If } from "lib/@components/switch"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { BadgeLabel } from "routes/regime/plugins/regime-title/badge-label"
import { setDraftStatusBadgeColor } from "authoring/routes/project/scoped-schedules/lib/set-draft-status-badge-color"

export function AuthoringScheduleSelectorItem({
    item,
    onSelect = noop,
    disabled = false,
    showCode = false,
    primarySx,
}) {
    const { selectedItems } = useBoundContext()
    const { label, title, id, code, projectId, projectName, draftStatus } = item
    const isInUseByAnotherProject = projectId || projectName ? true : false
    const isNonEditable = draftStatus === DraftStatus.SUBMITTED || draftStatus === DraftStatus.APPROVED
    const isItemDisabled = disabled || isNonEditable

    const isSelected = selectedItems.some(({ id: selectedId }) => selectedId === id)

    const itemTextSecondary =
        showCode && `${code} ${isInUseByAnotherProject ? `- In use by ${projectName || projectId}` : ""}`

    return (
        <BlockKeyboard>
            <Tooltip title={isNonEditable ? getDisabledMessage(draftStatus) : ""} disableHoverListener={!isNonEditable}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={handleClick}
                        data-cy={`authoring-schedule-selector-item-${id}`}
                        dense
                        disabled={isNonEditable}
                    >
                        <ListItemIcon>
                            <Stack direction={"row"} alignItems={"center"} spacing={1}>
                                <Checkbox
                                    edge="start"
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={isItemDisabled}
                                />
                                <Iconify icon={"quill:paper"} sx={{ width: 25, height: 25 }} />
                            </Stack>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Stack direction="row" sx={primarySx}>
                                    <TruncatedTypography>{label || title}</TruncatedTypography>
                                    <ScheduleSummary id={id?.toString()} />
                                </Stack>
                            }
                            secondary={itemTextSecondary}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                                variant="body2"
                                data-cy="authoring-schedule-selector-code"
                            >{`#${code}`}</Typography>
                            <If value={draftStatus}>
                                <BadgeLabel
                                    variant="subtitle2"
                                    sx={{ zoom: 0.8 }}
                                    color={setDraftStatusBadgeColor(draftStatus)}
                                >
                                    {draftStatus?.titleize()}
                                </BadgeLabel>
                            </If>
                        </Stack>
                    </ListItemButton>
                </ListItem>
            </Tooltip>
        </BlockKeyboard>
    )

    function handleClick() {
        !isItemDisabled && onSelect(item)
    }
}

function getDisabledMessage(draftStatus) {
    if (draftStatus === DraftStatus.SUBMITTED)
        return "This schedule has been submitted for review and cannot be added to a group"
    if (draftStatus === DraftStatus.APPROVED) return "This schedule has been approved and cannot be added to a group"
    return ""
}
